/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class VIPRBundleHelper
 * @classdesc implementation helper methods to get all bundles and add, update, and delete custom visualization.
 */

define([
	'./VIPRConfig',
	'../../DynamicFileLoader',
	'../../widgets/livewidget/nls/StringResources',
	'underscore'
],
(VIPRConfig, DynamicFileLoader, StringResources, _) => {
	const baseUrl = 'v1/visualizations';

	const _logError = (dashboardApi, error) => {
		const logger = dashboardApi.getGlassCoreSvc('.Logger');
		logger.error(error);
	};

	return class VIPRBundleHelper {

		/**
		 * @function VIPRBundleHelper#_loadVIPRLibrariesClass
		 * @private
		 * @description load module VIPRLibraries.
		 *
		 * @return {Promise} Promise that will resolve once the class VIPRLibraries has been loaded
		 */
		static _loadVIPRLibrariesClass() {
			return DynamicFileLoader.load(['dashboard-analytics/visualizations/vipr/VIPRLibraries'])
				.then((modules) => {
					return modules[0];
				})
				.catch((err) => {
					console.log(err);
				});
		}

		/**
		 * @function VIPRBundleHelper#_getVIPRLibraries
		 * @private
		 * @description get list of vipr visualizations.
		 *
		 * @return {Promise} Promise that will resolve once the vipr visualization are loaded
		 */
		static _getVIPRLibraries(options) {
			return VIPRBundleHelper._loadVIPRLibrariesClass()
				.then((LibrariesClass) => {
					let viprLibrariesClass = new LibrariesClass({ dashboardApi: options.dashboardApi });
					return viprLibrariesClass.setVIPRLibraries();
				})
				.catch((err) => {
					_logError(options.dashboardApi, err);
				});
		}

		/**
		* @function _getOnlyEnabledSystemVisualizations
		* @private
		* @description Remove any visualizations that are not feature enabled.
		* @param {Array} arrayOfVisDescriptions - array of visualzations that should be checked.
		* Any feature with the following property will be checked:
		* "featureFlag": {
		*	"id": "mentionsVis",
		*	"enabledVal": "enabled"
		*}
		* @param {Object} options - hold dashboardApi as a prop which is used for feature checker.
		*/
		static _getOnlyEnabledSystemVisualizations(arrayOfVisDescriptions, options) {
			const aEnabledVizs = arrayOfVisDescriptions.filter((vis) => {
				if (vis.featureFlag) {
					const featureFlag = vis.featureFlag ? vis.featureFlag.id : '';
					const valueToMatch = vis.featureFlag ? vis.featureFlag.enabledVal : 'enabled';
					return options.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', featureFlag, valueToMatch);
				} else {
					return true;
				}
			}
			);
			return aEnabledVizs;
		}

		/**
		 * @function VIPRBundleHelper#getALLVIPRLibraries
		 * @private
		 * @description get list of all available visualizations(custom, vipr).
		 *
		 * @return {Promise} Promise that will resolve once the all visualization are loaded
		 */
		static getALLVIPRLibraries(options = {}) {
			const libraries = [];

			const promises = [VIPRBundleHelper._getVIPRLibraries(options)];

			promises.push(VIPRBundleHelper.getCustomBundleVisualizations(options));

			return Promise.all(promises)
				.then(results => {
					const standardLibraries = results[0];
					const customLibraries = results[1];
					// Remove any visualizations that are not feature enabled.
					const enabledSystemVizs = VIPRBundleHelper._getOnlyEnabledSystemVisualizations(standardLibraries.system, options);
					libraries.push(...enabledSystemVizs);

					// process configurations defined in definitions to extend or add VIPRConfig configurations
					VIPRBundleHelper._extendVIPRConfig(enabledSystemVizs);

					if (standardLibraries['custom'].length > 0) {
						VIPRBundleHelper._buildCustomLibVIPRConfig(standardLibraries.custom || []);
						libraries.push(...standardLibraries['custom']);
					}

					if (customLibraries.length > 0) {
						libraries.push(...customLibraries);
					}
					return libraries;
				})
				.catch((err) => {
					_logError(options.dashboardApi, err);
					throw new Error(err);
				});
		}

		static _extendVIPRConfig(visualizationLibs) {
			visualizationLibs.forEach(lib => {
				if (lib.configuration) {
					VIPRConfig.extendConfig(lib);
				}
			});
		}

		/**
		 * @function VIPRBundleHelper#getCustomBundleVisualizations
		 * @public
		 * @description Make rest call to get list of custom visualizations bundle information from the server.
		 *
		 * @returns {Promise} Returns a promise resolved with a JSON object contains custom bundles information from server
		 */
		static getCustomBundleVisualizations(options) {
			return VIPRBundleHelper._getCustomVisBundle(options)
				.then(customVisualizations => {
					return VIPRBundleHelper._buildCustomBundleVisualizationsLib(options.ajax, customVisualizations);
				})
				.catch(error => {
					_logError(options.dashboardApi, error);
					return Promise.resolve([]);
				});
		}

		static _getCustomVisBundle(options){
			const endpoint = `${baseUrl}/custom`;
			const ajax = options.ajax;
			let promise = null;
			/**
			 * When a refresh is not requested, use the preFetched custom visdef if one exists
			 * else make an ajax request.
			 */
			if(!options.refresh){
				const preFetch = options.preFetch;
				promise = preFetch && preFetch.get(endpoint);
				promise && preFetch.clear(endpoint);
			}

			/**
			 * An ajax request will be made when the refresh is true as in the case when the dashboard is reloaded and 
			 * a custom visulization is used on the dashboard. 
			 * 
			 */
			return promise || ajax.ajax({
				url: endpoint,
				type: 'GET',
				contentType: 'application/json'
			});
		}

		static _buildCustomBundleVisualizationsLib(ajaxSvc, customVisualizationslib = {}) {
			const customCustomVisualizationsLib = [];
			const data = customVisualizationslib.data || [];
			let promise;
			if (data.length === 0) {
				promise = Promise.resolve(customCustomVisualizationsLib);
			} else {
				_.forEach(data, (lib) => {
					customCustomVisualizationsLib.push({
						id: lib.id,
						location: lib.type === 'schematic' ? 'vizbundles/rave2schematics' : 'v1/visualizations/' + lib.id + '/content/',
						assetId: lib.storeId,
						icon: lib.icon || null,
						placeholderIcon: 'v1/visualizations/' + lib.id + '/content/',
						modificationTime: lib.modificationTime,
						name: lib.name,
						type: lib.id,
						isSchematic: lib.type === 'schematic',
						isCustomVis: lib.type !== 'schematic'
					});
				});

				promise = VIPRBundleHelper._loadIcon(ajaxSvc, customCustomVisualizationsLib);
			}

			return promise
				.then(VIPRBundleHelper._buildCustomLibVIPRConfig);
		}

		static _loadIcon (ajaxSvc, customCustomVisualizationsLib = []) {
			const promises = [];
			customCustomVisualizationsLib.forEach(lib => {
				if (!lib.icon && !lib.isSchematic) {
					//Load only if one not found,
					//This happens when the icon is defined at nested folder levels and not at the root
					//when the custom vis was storeed in CM
					const url = baseUrl  + '/id/' + lib.id;
					promises.push(ajaxSvc.ajax({
						url: url,
						type: 'GET',
						contentType: 'application/json'
					}));
				} else {
					promises.push(Promise.resolve(null));
				}
			});
			return Promise.all(promises)
				.then((responses = []) => {
					responses.forEach((response, idx) => {
						if (response) {
							const data = response.data || [];
							const icon = data.find(fileDesc => {
								fileDesc = fileDesc || {};
								const file = fileDesc.file ? fileDesc.file.toLowerCase() : '';
								return VIPRBundleHelper.isValidIconFileExt(file);
							});

							if (icon) {
								const nameParts = icon.file.split('\\');
								customCustomVisualizationsLib[idx].icon = nameParts.join('/');
							}
						}
					});
					return customCustomVisualizationsLib;
				});
		}

		static _buildCustomLibVIPRConfig(customCustomVisualizationsLibs = []) {
			customCustomVisualizationsLibs.forEach(lib => {
				VIPRConfig.removeCustomVisConfiguration(lib.id);
				VIPRConfig.addCustomVisConfiguration(lib);
			});
			return customCustomVisualizationsLibs;
		}

		/**
		 * @function VIPRBundleHelper#loadCustomBundleVisulization
		 * @public
		 * @description Make rest call to load custom visualization to the server.
		 *
		 * @param {Object} options contains dashboardApi and data of type FromData of selected visualization
		 * @returns {Promise} Returns a promise resolved with id of custom bundles information added on server
		 */
		static loadCustomBundleVisualization(options) {
			return VIPRBundleHelper.submitAjaxAddOrUpdate(options, 'POST', baseUrl + '?maskResponses=true&updateAction=FAIL');
		}

		/**
		 * @function VIPRBundleHelper#updateCustomBundleVisualization
		 * @public
		 * @description Make rest call to load custom visualization to the server.
		 *
		 * @param {Object} options contains dashboardApi and data of type FromData of selected visualization
		 * @returns {Promise} Returns a promise resolved with id of custom bundles information added on server
		 */
		//@todo for R3 will use POST for updating, VIDA will fix the ajax call to use put with dedicated update endpoint
		static updateCustomBundleVisualization(options = {}) {
			return VIPRBundleHelper.submitAjaxAddOrUpdate(options, 'PUT', baseUrl + '/id/' + options.bundleId);
		}

		/**
		 * @function VIPRBundleHelper#deleteCustomBundleVisulization
		 * @public
		 * @description Make rest call to delete custom visualization bundle information from the server.
		 *
		 * @returns {Promise} Returns a promise resolved if custom visualization bundle information deleted from the server,
		 * promise rejected if custom visualization bundle information is not deleted from the server
		 */
		static deleteCustomBundleVisulization(options = {}) {
			const ajaxSvc = options.dashboardApi.getGlassCoreSvc('.Ajax');
			const userCapabilities = options.dashboardApi.getGlassCoreSvc('.UserProfile').capabilities;
			const hasPermission = userCapabilities.indexOf('canManageVisualizations') !== -1;
			if (hasPermission) {
				return ajaxSvc.ajax({
					url: 'v1/objects/' + encodeURIComponent(options.assetId),
					type: 'DELETE'
				}).then(() => {
					VIPRConfig.removeCustomVisConfiguration(options.id);
				});
			} else {
				return Promise.reject(new Error(StringResources.get('noPermissionToManage')));
			}
		}

		static isValidIconFileExt = (file) => {
			const supportIconFileExts = ['.png', '.svg'];

			return file && !!supportIconFileExts.find(ext => {
				return file.indexOf(ext) !== -1;
			});
		}

		static submitAjaxAddOrUpdate (options = {}, type, url) {
			const userCapabilities = options.dashboardApi.getGlassCoreSvc('.UserProfile').capabilities;
			const hasPermission = userCapabilities.indexOf('canManageVisualizations') !== -1;

			if (hasPermission) {
				const ajaxSvc = options.dashboardApi.getGlassCoreSvc('.Ajax');
				return ajaxSvc.ajax({
					url: url,
					type: type,
					data: options.data,
					enctype: 'multipart/form-data',
					processData: false,
					contentType: false
				}).then(info => {
					const data = JSON.parse(info.data);
					if (data.Status === 200 || data.Status === 201) {
						return data;
					} else if (info.jqXHR && info.jqXHR.responseJSON) {
						return Promise.reject(new Error(info.jqXHR.responseJSON.msg));
					} else if (info.jqXHR && info.jqXHR.responseText) {
						return Promise.reject(new Error(info.jqXHR.responseText));
					} else {
						return Promise.reject(new Error(data.Description));
					}
				});
			} else {
				return Promise.reject(new Error(StringResources.get('noPermissionToManage')));
			}
		}
	};
});
